import React, { useState, useEffect, useRef, useMemo } from 'react';
import styles from './index.module.scss';
import { renderItalicizedText } from '@buzzfeed/bf-utils';
import { ANIMATION_TIMINGS, ANIMATION_STATES, MODAL_STATES } from '../constants';
import { CloseIcon, ThumbUpIcon, ThumbDownIcon } from '../../../../icons/FeedbackIcons';
import { useTrackingContext } from '../../../../hooks';



// Add display names for debugging
CloseIcon.displayName = 'CloseIcon';
ThumbUpIcon.displayName = 'ThumbUpIcon';
ThumbDownIcon.displayName = 'ThumbDownIcon';

const FeedbackModal = ({
  isVisible,
  onClose,
  onAnimationStateChange,
  title = "Tell us what you're into.",
  subtitle = "We'll give you more of that good stuff.",
  // imageUrl,
  // artifactId,
  trackingInfo,
  skipAnimation = false
}) => {
  // Unified animation state machine
  const [animationPhase, setAnimationPhase] = useState(ANIMATION_STATES.IDLE);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  const { trackContentAction } = useTrackingContext();

  // Memoized title with italicized vowels for performance
  const memoizedTitle = useMemo(() => renderItalicizedText(title), [title]);

  // Derived state from animation phase
  const showBackground = animationPhase === ANIMATION_STATES.CONTENT_VISIBLE;
  const showContent = animationPhase === ANIMATION_STATES.CONTENT_VISIBLE;

  // Animation sequence when modal becomes visible
  useEffect(() => {
    if (!isVisible) return;

    if (skipAnimation) {
      // Skip to final state for returning users
      setAnimationPhase(ANIMATION_STATES.CONTENT_VISIBLE);
      onAnimationStateChange?.(MODAL_STATES.START_BLUR);
      onAnimationStateChange?.(MODAL_STATES.END_BLUR);
      return;
    }
    // Start animation sequence
    // Step 1: Blur all content
    setAnimationPhase(ANIMATION_STATES.BLURRING);
    onAnimationStateChange?.(MODAL_STATES.START_BLUR);
    
    const step2Timer = setTimeout(() => {
      // Step 2: Start image keyframe animation
      setAnimationPhase(ANIMATION_STATES.ANIMATING);
      onAnimationStateChange?.(MODAL_STATES.START_IMAGE_ANIMATION);
      
      const step3Timer = setTimeout(() => {
        // Step 3-5: Show background, unblur content, and show modal content simultaneously
        setAnimationPhase(ANIMATION_STATES.CONTENT_VISIBLE);
        onAnimationStateChange?.(MODAL_STATES.END_BLUR);
        
      }, ANIMATION_TIMINGS.IMAGE_ANIMATION_TRIGGER_DELAY);

      return () => clearTimeout(step3Timer);
    }, ANIMATION_TIMINGS.BRIEF_DELAY);

    return () => clearTimeout(step2Timer);
  }, [isVisible, onAnimationStateChange, skipAnimation]);

  const handleClose = () => {
    setIsClosing(true);
    
    // Step 1: Hide content and background first
    setAnimationPhase(ANIMATION_STATES.CLOSING);
    onAnimationStateChange?.(MODAL_STATES.CLOSING);
    
    // Step 2: After content disappears, start reverse image animation
    setTimeout(() => {
      onAnimationStateChange?.(MODAL_STATES.REVERSING);
      
      // Step 3: After reverse animation completes, fully close modal
      setTimeout(() => {
        onClose();
      }, ANIMATION_TIMINGS.REVERSE_ANIMATION_DURATION);
    }, ANIMATION_TIMINGS.CONTENT_FADEOUT_DELAY);
  };

  const handleFeedback = (type) => {
    // Track feedback event using the same pattern as Artifact component
    if (trackContentAction) {
      const actionValue = type === 'more' ? 'positive' : 'negative';
      
      const trackingData = {
        ...trackingInfo,
        item_type: 'button',
        item_name: 'feedback',
        position_in_subunit: null,
        action_type: 'select',
        action_value: actionValue,
      };
      
      trackContentAction(trackingData);
    }
    
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <div 
      ref={modalRef}
      className={`${styles.modalOverlay} ${isClosing ? styles.closing : ''}`}
    >
      {/* Gradient background that appears in step 3 */}
      <div className={`${styles.gradientBackground} ${showBackground ? styles.visible : ''}`} />
      
      {/* Close button - appears with background */}
      <button 
        onClick={handleClose}
        className={`${styles.closeButton} ${showBackground ? styles.visible : ''}`}
        aria-label="Close feedback modal"
      >
        <CloseIcon />
      </button>

      {/* Content that appears in step 5 - positioned to appear below shrunk image */}
      <div className={`${styles.modalContent} ${showContent ? styles.visible : ''}`}>
        <h3
          className={styles.title}
          dangerouslySetInnerHTML={{ __html: memoizedTitle }}
        />
        <p className={styles.subtitle}>{subtitle}</p>
        
        <div className={styles.buttons}>
          <button 
            onClick={() => handleFeedback('more')}
            className={styles.feedbackButton}
          >
            <span className={styles.icon}>
              <ThumbUpIcon />
            </span>
            More, please
          </button>
          
          <button 
            onClick={() => handleFeedback('blast')}
            className={styles.feedbackButton}
          >
            <span className={styles.icon}>
              <ThumbDownIcon />
            </span>
            Blast this into the sun
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FeedbackModal);
